import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@env';
import { ClaveAlmacenamiento } from '@nucleo/modelos/clave-almacenamiento.enum';
import { PaginaRespuestaDto, TAMANO_MAXIMO_PAGINA } from '@nucleo/http/pagina-respuesta-dto';

export interface ConjuntoResumenDto {
  id: string;
  name: string;
}

const RUTA_CONJUNTOS = '/complexes';
/** Conjuntos de las unidades del propio usuario: lo usa el residente, que no administra conjuntos. */
const RUTA_MIS_CONJUNTOS = '/me/complexes';

/**
 * Conjunto (condominio) con el que se trabaja. Casi toda la información de Comunia pertenece a un conjunto, así que el
 * usuario elige uno en la barra superior y las pantallas filtran y crean registros dentro de él. La elección se recuerda
 * en este navegador. Los datos de los conjuntos los sirve el servicio de propiedades.
 */
@Injectable({ providedIn: 'root' })
export class ConjuntoActivoServicio {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apisBase.propiedades.url}${RUTA_CONJUNTOS}`;

  readonly conjuntos = signal<ConjuntoResumenDto[]>([]);
  readonly activoId = signal<string | null>(this.leerGuardado());
  readonly cargando = signal(false);
  readonly sinPermiso = signal(false);

  /**
   * Carga la lista de conjuntos y deja seleccionado el recordado o, si ya no existe, el primero. Quien no puede
   * administrar conjuntos (un residente) recibe los de sus propias unidades.
   */
  cargar(): void {
    this.cargando.set(true);
    this.http
      .get<PaginaRespuestaDto<ConjuntoResumenDto>>(this.url, {
        params: { size: TAMANO_MAXIMO_PAGINA, sort: 'name' },
      })
      .subscribe({
        next: (pagina) => this.aplicarConjuntos(pagina.content),
        error: () => this.cargarMisConjuntos(),
      });
  }

  private cargarMisConjuntos(): void {
    this.http.get<ConjuntoResumenDto[]>(`${environment.apisBase.propiedades.url}${RUTA_MIS_CONJUNTOS}`).subscribe({
      next: (conjuntos) => this.aplicarConjuntos(conjuntos),
      error: () => {
        this.sinPermiso.set(true);
        this.conjuntos.set([]);
        this.cargando.set(false);
      },
    });
  }

  private aplicarConjuntos(lista: ConjuntoResumenDto[]): void {
    this.sinPermiso.set(false);
    this.conjuntos.set(lista.map((c) => ({ id: c.id, name: c.name })));
    const actual = this.activoId();
    if (!actual || !lista.some((c) => c.id === actual)) {
      this.seleccionar(lista[0]?.id ?? null);
    }
    this.cargando.set(false);
  }

  seleccionar(id: string | null): void {
    this.activoId.set(id);
    try {
      if (id) localStorage.setItem(ClaveAlmacenamiento.CONJUNTO_ACTIVO, id);
      else localStorage.removeItem(ClaveAlmacenamiento.CONJUNTO_ACTIVO);
    } catch {
      // Sin acceso a localStorage: la elección dura solo mientras la pestaña esté abierta.
    }
  }

  private leerGuardado(): string | null {
    try {
      return localStorage.getItem(ClaveAlmacenamiento.CONJUNTO_ACTIVO);
    } catch {
      return null;
    }
  }
}
